import { GraphQLVehicleTagsRequester } from '@cobliteam/api-lib';
import type { VehicleTag } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const QUERY_KEY = 'VEHICLE_TAGS';

// Lazy instantiation to avoid circular dependency issues
const getGraphQLVehicleTagsRequester = () => new GraphQLVehicleTagsRequester();

const useVehicleTags = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('vehicles');

  const getVehicleTags = (options?: Partial<UseQueryOptions<VehicleTag[]>>) =>
    useQuery<VehicleTag[]>({
      queryKey: [QUERY_KEY],
      ...options,
      queryFn: async () => getGraphQLVehicleTagsRequester().getVehicleTags(),
    });

  const useDeleteVehicleTag = () => {
    return useMutation<string, unknown, string>({
      mutationFn: async (tagName: string) => await getGraphQLVehicleTagsRequester().deleteVehicleTag(tagName),
      onSuccess: (deletedTagName) => {
        Alert.success(t('tags.tag_removed'));
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      },
      onError: () => {
        Alert.error(t('tags.tag_removed_failed'));
      },
    });
  };

  return {
    getVehicleTags,
    useDeleteVehicleTag,
  };
};

export default useVehicleTags;
